import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import {
  Mutation,
  MutationSetInstallerArgs,
  MutationSetVisitorArgs,
  MutationSetVisitorByIdArgs,
  MutationUnsetInstallerByIdArgs,
  Query,
  QueryGetVisitorGroupsForTenantArgs
} from 'graphql/generated/graphqlTypes';
import { SET_INSTALLER, SET_VISITOR, SET_VISITOR_BY_ID, UNSET_INSTALLER_BY_ID } from 'graphql/mutations';
import { GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS } from 'graphql/queries';
import { useEffect } from 'react';
import {
  setInstallersGroupsAction,
  setInstallersMemberAction,
  unsetInstallerByIdAction
} from 'state/actions/installersGroups';
import { installersGroupsVar } from 'state/vars';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import { BaseHookProps } from '../shared/types';

// Typing the document (instead of passing generics to useMutation) avoids the deprecated useMutation signature.
const SET_VISITOR_TYPED: TypedDocumentNode<Pick<Mutation, 'setVisitor'>, MutationSetVisitorArgs> = SET_VISITOR;
const GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  QueryGetVisitorGroupsForTenantArgs
> = GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS;
const SET_INSTALLER_TYPED: TypedDocumentNode<Pick<Mutation, 'setInstaller'>, MutationSetInstallerArgs> = SET_INSTALLER;
const SET_VISITOR_BY_ID_TYPED: TypedDocumentNode<
  Pick<Mutation, 'setVisitorById'>,
  MutationSetVisitorByIdArgs
> = SET_VISITOR_BY_ID;
const UNSET_INSTALLER_BY_ID_TYPED: TypedDocumentNode<
  Pick<Mutation, 'unsetInstallerById'>,
  MutationUnsetInstallerByIdArgs
> = UNSET_INSTALLER_BY_ID;

const useInstallers = ({ handleFetchError }: BaseHookProps) => {
  const installersGroups = useReactiveVar(installersGroupsVar);

  const [getInstallersMembers, { loading }] = useLazyQuery(GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_TYPED);

  useEffect(() => {
    const fetchInstallersMembers = async () => {
      try {
        const { data } = await getInstallersMembers({
          variables: {
            filter: { type: VisitorGroupType.Installers }
          }
        });
        if (data) {
          setInstallersGroupsAction(data.getVisitorGroupsForTenant);
        }
      } catch {
        handleFetchError('Error while fetching installers');
      }
    };
    void fetchInstallersMembers();
  }, [getInstallersMembers]);

  const installersGroupExternalRef = installersGroups.find(
    (group) => group.type === VisitorGroupType.Installers
  )?.externalRef;

  const [setInstallerMutation, { loading: setInstallerMemberLoading }] = useMutation(SET_INSTALLER_TYPED, {
    onCompleted: (data) => data,
    onError: () => {
      handleFetchError('Error while setting installer');
    }
  });
  const setInstallerMember = async (email: string, name: string) => {
    const { data } = await setInstallerMutation({
      variables: { email, name },
      update(cache, { data: updatedMember }) {
        const updatedGroups = installersGroups.map((group) => {
          if (group.externalRef === installersGroupExternalRef)
            if (group.members.some((member) => member.email === updatedMember?.setInstaller.email))
              return {
                ...group,
                members: group.members.map((member) =>
                  member.email === updatedMember?.setInstaller.email ? updatedMember : member
                )
              };
            else
              return {
                ...group,
                members: { ...group.members, updatedMember }
              };
          return {
            ...group,
            members: group.members.filter((member) => member.email !== updatedMember?.setInstaller.email)
          };
        });
        cache.writeQuery({
          query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS,
          variables: {
            filter: { type: VisitorGroupType.Installers }
          },
          data: {
            getVisitorGroupsForTenant: updatedGroups
          }
        });
      }
    });
    if (data) {
      setInstallersMemberAction(data.setInstaller, [installersGroupExternalRef!]);
    }
    return data;
  };

  const [updateInstallerMutation, { loading: updateInstallerMemberLoading }] = useMutation(SET_VISITOR_TYPED, {
    onCompleted: (data) => data,
    onError: () => handleFetchError('Error while updating installer')
  });
  const updateInstallerMember = async (email: string, name: string) => {
    const { data } = await ignoreHandledMutationError(
      updateInstallerMutation({
        variables: { email, name, visitorGroupExternalRefs: [installersGroupExternalRef!] },
        update(cache, { data: updatedMember }) {
          const updatedGroups = installersGroups.map((group) => {
            if (group.externalRef === installersGroupExternalRef)
              if (group.members.some((member) => member.email === updatedMember?.setVisitor.email))
                return {
                  ...group,
                  members: group.members.map((member) =>
                    member.email === updatedMember?.setVisitor.email ? updatedMember : member
                  )
                };
              else
                return {
                  ...group,
                  members: { ...group.members, updatedMember }
                };
            return {
              ...group,
              members: group.members.filter((member) => member.email !== updatedMember?.setVisitor.email)
            };
          });
          cache.writeQuery({
            query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS,
            variables: {
              filter: { type: VisitorGroupType.Installers }
            },
            data: {
              getVisitorGroupsForTenant: updatedGroups
            }
          });
        }
      })
    );
    if (data) {
      setInstallersMemberAction(data.setVisitor, [installersGroupExternalRef!]);
    }
    return data;
  };

  const [updateInstallerByIdMutation, { loading: updateInstallerByIdMemberLoading }] = useMutation(
    SET_VISITOR_BY_ID_TYPED,
    {
      onCompleted: (data) => data,
      onError: () => handleFetchError('Error while updating installer')
    }
  );
  const updateInstallerByIdMember = async (visitorId: string, name: string) => {
    const { data } = await updateInstallerByIdMutation({
      // TODO:
      // @ts-ignore
      variables: { visitorId, name, visitorGroupExternalRefs: [installersGroupExternalRef!] },
      update(cache, { data: updatedMember }) {
        const updatedGroups = installersGroups.map((group) => {
          if (group.externalRef === installersGroupExternalRef)
            if (group.members.some((member) => member.id === updatedMember?.setVisitorById.id))
              return {
                ...group,
                members: group.members.map((member) =>
                  member.id === updatedMember?.setVisitorById.id ? updatedMember : member
                )
              };
            else
              return {
                ...group,
                members: { ...group.members, updatedMember }
              };
          return {
            ...group,
            members: group.members.filter((member) => member.id !== updatedMember?.setVisitorById.id)
          };
        });
        cache.writeQuery({
          query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS,
          variables: {
            filter: { type: VisitorGroupType.Installers }
          },
          data: {
            getVisitorGroupsForTenant: updatedGroups
          }
        });
      }
    });
    if (data) {
      setInstallersMemberAction(data.setVisitorById, [installersGroupExternalRef!]);
    }
    return data;
  };

  const [
    unsetInstallerByIdMutation,
    { error: unsetInstallerByIdMutationError, loading: unsetInstallerByIdMutationLoading }
  ] = useMutation(UNSET_INSTALLER_BY_ID_TYPED, {
    onCompleted: (data) => {
      unsetInstallerByIdAction(data.unsetInstallerById.id);
    },
    onError: () => {
      handleFetchError('Error while deleting installer');
    }
  });

  const unsetInstallerById = async (visitorId: string) => {
    const { data } = await ignoreHandledMutationError(
      unsetInstallerByIdMutation({
        variables: { visitorId },
        update(cache, { data: deletedMemberData }) {
          const groupWithDeletedMember = installersGroups.filter((group) =>
            group.members.some((member) => member.id === visitorId)
          )[0];
          const updatedGroups = installersGroups.map((group) => {
            if (group.id === groupWithDeletedMember.id)
              return {
                ...group,
                members: group.members.filter((member) => member.id !== deletedMemberData?.unsetInstallerById.id)
              };
            return group;
          });

          cache.writeQuery({
            query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS,
            variables: {
              filter: { type: VisitorGroupType.Installers }
            },
            data: {
              getVisitorGroupsForTenant: updatedGroups
            }
          });
        }
      })
    );
    return data;
  };

  return {
    installersGroups,
    setInstallerMember,
    setInstallerMemberLoading,
    unsetInstallerById,
    unsetInstallerByIdMutationLoading,
    unsetInstallerByIdMutationError,
    loading,
    updateInstallerMember,
    updateInstallerMemberLoading,
    updateInstallerByIdMember,
    updateInstallerByIdMemberLoading
  };
};

export default useInstallers;
